import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Upload, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rich text editor toolbar configuration (matches Blog/News editor)
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};
const quillFormats = ['header', 'bold', 'italic', 'strike', 'list', 'link'];

// Slugify helper for the admin's live preview
const slugifyPreview = (text) => {
  if (!text) return '';
  return text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
};

const PortfolioManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    address: '',
    units: '',
    square_feet: '',
    year: '',
    status: 'Upcoming',
    description: '',
    amenities: [],
    highlights: [],
    image_url: '',
    images: [],
    location: { lat: '', lng: '' },
    home_featured: false,
    home_display_order: '',
    featured: false
  });
  const [amenitiesInput, setAmenitiesInput] = useState('');
  const [highlightsInput, setHighlightsInput] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: { ...prev.location, [field]: value }
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('admin_token');
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/admin/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        // Handle both Cloudinary URLs (full URLs) and local URLs (relative paths)
        const url = response.data.url;
        uploadedUrls.push(url.startsWith('http') ? url : `${BACKEND_URL}${url}`);
      }

      setFormData(prev => {
        const allImages = [...prev.images, ...uploadedUrls];
        return {
          ...prev,
          images: allImages,
          image_url: allImages[0] // Always use first image as thumbnail
        };
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      if (error.response && error.response.status === 401) {
        alert('Session expired. Please log in again.');
        localStorage.removeItem('admin_token');
        window.location.href = '/admin';
      } else {
        alert('Error uploading images: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: newImages,
        image_url: newImages[0] || '' // Update thumbnail to first remaining image
      };
    });
  };

  const handleAmenitiesInput = () => {
    if (amenitiesInput.trim()) {
      // Split by comma, trim each item, and filter out empty strings
      const newAmenities = amenitiesInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      setFormData(prev => ({
        ...prev,
        amenities: [...(prev.amenities || []), ...newAmenities]
      }));
      setAmenitiesInput('');
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleHighlightsInput = () => {
    if (highlightsInput.trim()) {
      const newHighlights = highlightsInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      setFormData(prev => ({
        ...prev,
        highlights: [...(prev.highlights || []), ...newHighlights]
      }));
      setHighlightsInput('');
    }
  };

  const removeHighlight = (index) => {
    setFormData(prev => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index)
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    const submitData = {
      ...formData,
      units: formData.units ? parseInt(formData.units) : null,
      home_display_order: formData.home_display_order ? parseInt(formData.home_display_order) : null,
      location: formData.location.lat && formData.location.lng 
        ? { lat: parseFloat(formData.location.lat), lng: parseFloat(formData.location.lng) }
        : null
    };

    try {
      if (editingProject) {
        await axios.put(`${API}/admin/projects/${editingProject.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Property updated successfully!', 'success');
      } else {
        await axios.post(`${API}/admin/projects`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Property added successfully!', 'success');
      }

      fetchProjects();
      closeModal();
    } catch (error) {
      console.error('Error saving project:', error);
      if (error.response && error.response.status === 401) {
        showNotification('Session expired. Please log in again.', 'error');
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          window.location.href = '/admin';
        }, 2000);
      } else {
        showNotification('Error saving project: ' + (error.response?.data?.detail || error.message), 'error');
      }
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Property deleted successfully!', 'success');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      if (error.response && error.response.status === 401) {
        showNotification('Session expired. Please log in again.', 'error');
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          window.location.href = '/admin';
        }, 2000);
      } else {
        showNotification('Error deleting project: ' + (error.response?.data?.detail || error.message), 'error');
      }
    }
  };

  const openModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name || '',
        slug: project.slug || '',
        address: project.address || '',
        featured: project.featured || false,
        units: project.units?.toString() || '',
        square_feet: project.square_feet || '',
        year: project.year || '',
        status: project.status || '',
        description: project.description || '',
        amenities: project.amenities || [],
        highlights: project.highlights || [],
        image_url: project.image_url || '',
        images: project.images || [],
        location: project.location 
          ? { 
              lat: project.location.lat?.toString() || '', 
              lng: project.location.lng?.toString() || '' 
            }
          : { lat: '', lng: '' },
        home_featured: project.home_featured || false,
        home_display_order: project.home_display_order || ''
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        slug: '',
        address: '',
        featured: false,
        units: '',
        square_feet: '',
        year: '',
        status: 'Upcoming',
        description: '',
        amenities: [],
        highlights: [],
        image_url: '',
        images: [],
        location: { lat: '', lng: '' },
        home_featured: false,
        home_display_order: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-12 w-12 border-4 border-[#20B2AA] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Success/Error Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-in slide-in-from-top ${
          notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {notification.type === 'success' && <CheckCircle className="h-5 w-5" />}
          {notification.type === 'error' && <X className="h-5 w-5" />}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-black mb-2">Portfolio Management</h1>
          <p className="text-gray-600">Manage your property portfolio</p>
        </div>
        <Button
          onClick={() => openModal()}
          className="bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-xl px-6 py-6 shadow-lg"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Project
        </Button>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
            <div className="relative h-48">
              <img
                src={project.image_url || 'https://via.placeholder.com/400x300'}
                alt={project.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3 flex gap-2">
                <div className="px-3 py-1 bg-black/70 text-white text-xs rounded-full">
                  {project.status}
                </div>
                {project.featured && (
                  <div className="px-3 py-1 bg-[#20B2AA] text-white text-xs rounded-full">
                    Featured
                  </div>
                )}
              </div>
            </div>
            <div className="p-5">
              <h3 className="font-bold text-lg text-black mb-2 line-clamp-1">{project.name}</h3>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{project.address}</p>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => openModal(project)}
                  className="flex-1 bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-lg"
                  size="sm"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(project.id)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                  size="sm"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-2xl font-bold text-black">
                {editingProject ? 'Edit Project' : 'Add New Project'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name *</label>
                  <textarea
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Enter project name (use Enter for line breaks)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA] resize-y"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Press Enter to add line breaks</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                    required
                  >
                    <option value="Upcoming">Upcoming</option>
                    <option value="Under Construction">Under Construction</option>
                    <option value="Completed">Completed</option>
                    <option value="Affordable Housing">Affordable Housing</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="w-5 h-5 text-[#20B2AA] border-gray-300 rounded focus:ring-[#20B2AA]"
                    />
                    <span>Featured Listing</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-7">Display this property in featured sections</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  required
                />
              </div>

              {/* SEO Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO URL Slug
                </label>
                <div className="flex items-center rounded-xl border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#20B2AA]">
                  <span className="px-3 py-3 bg-gray-100 text-gray-500 text-sm border-r border-gray-300 whitespace-nowrap">
                    /portfolio/
                  </span>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: slugifyPreview(e.target.value) }))}
                    placeholder={slugifyPreview(formData.name) || 'auto-generated-from-name'}
                    className="flex-1 px-4 py-3 focus:outline-none"
                    data-testid="portfolio-slug-input"
                  />
                  {!formData.slug && formData.name && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, slug: slugifyPreview(prev.name) }))}
                      className="px-3 py-3 text-xs text-[#20B2AA] hover:bg-[#20B2AA]/10 border-l border-gray-300"
                    >
                      Auto
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Custom URL for SEO. Only lowercase letters, numbers, and dashes. Leave blank to auto-generate from name.
                </p>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Units</label>
                  <input
                    type="number"
                    name="units"
                    value={formData.units}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Square Feet</label>
                  <input
                    type="text"
                    name="square_feet"
                    value={formData.square_feet}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <input
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                </div>
              </div>

              {/* Amenities Section */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">Amenities</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={amenitiesInput}
                    onChange={(e) => setAmenitiesInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAmenitiesInput())}
                    placeholder="Add amenities separated by commas (e.g., Pool, Gym, Parking)"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                  <button
                    type="button"
                    onClick={handleAmenitiesInput}
                    className="px-6 py-3 bg-[#20B2AA] text-white rounded-xl hover:bg-teal-600 transition-colors flex items-center gap-2"
                  >
                    <span className="text-xl">+</span> Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(formData.amenities || []).map((amenity, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg"
                    >
                      <span className="text-sm">{amenity}</span>
                      <button
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Property Highlights Section */}
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Highlights</label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={highlightsInput}
                    onChange={(e) => setHighlightsInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleHighlightsInput())}
                    placeholder="Add highlights separated by commas (e.g., Prime location, Modern design)"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  />
                  <button
                    type="button"
                    onClick={handleHighlightsInput}
                    className="px-6 py-3 bg-[#20B2AA] text-white rounded-xl hover:bg-teal-600 transition-colors flex items-center gap-2"
                  >
                    <span className="text-xl">+</span> Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(formData.highlights || []).map((highlight, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg"
                    >
                      <span className="text-sm">{highlight}</span>
                      <button
                        type="button"
                        onClick={() => removeHighlight(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (Rich Text)</label>
                <div className="border border-gray-300 rounded-xl quill-container">
                  <ReactQuill
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    placeholder="Describe this property... Use the toolbar for formatting."
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Use H1, H2, H3 for headings. Bold, Italic, Strike, and Links available.</p>
                <style>{`
                  .quill-container { position: relative; }
                  .quill-container .ql-picker-options { z-index: 9999 !important; position: absolute !important; }
                  .quill-container .ql-header .ql-picker-options {
                    background: white; border: 1px solid #ccc; border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                  }
                `}</style>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location Coordinates (Optional)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Latitude (e.g., 40.7476)"
                      value={formData.location.lat}
                      onChange={(e) => handleLocationChange('lat', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                    />
                    <p className="text-xs text-gray-500 mt-1">Positive = North, Negative = South</p>
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Longitude (e.g., -74.0377)"
                      value={formData.location.lng}
                      onChange={(e) => handleLocationChange('lng', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                    />
                    <p className="text-xs text-gray-500 mt-1">Positive = East, Negative = West</p>
                  </div>
                </div>
                <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700">
                    <strong>💡 Tip:</strong> For New Jersey properties, latitude is positive (North) and longitude is negative (West).
                    Example: 40.7476, -74.0377
                  </p>
                </div>
              </div>

              {/* Homepage Featured Toggle */}
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.home_featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, home_featured: e.target.checked, home_display_order: e.target.checked ? prev.home_display_order : '' }))}
                    className="w-5 h-5 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    🏠 Feature this building on Homepage
                  </span>
                </label>
                {formData.home_featured && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Display Order:</span>
                    <select
                      value={formData.home_display_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, home_display_order: e.target.value ? parseInt(e.target.value) : '' }))}
                      className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="">Auto</option>
                      <option value="1">1st</option>
                      <option value="2">2nd</option>
                      <option value="3">3rd</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Project Images
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#20B2AA] transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      {uploading ? 'Uploading...' : 'Click to upload images'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</p>
                  </label>
                </div>

                {/* Image Preview */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    {formData.images.map((url, index) => (
                      <div key={index} className="relative group">
                        <img src={url} alt={`Upload ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-black rounded-xl py-6"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-xl py-6"
                  disabled={uploading}
                >
                  {editingProject ? 'Update Project' : 'Create Project'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioManagement;
